import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar,
  ClipboardList,
  LogOut,
  Mail,
  ReceiptText,
  Star,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  const [activeView, setActiveView] = useState('bookings');

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAllData();
    } catch (error) {
      console.warn('Token invalid or expired:', error.response?.status);
      localStorage.removeItem('admin_token');
      toast.error('Session expired. Please login again.');
      navigate('/admin');
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('admin_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [bookingsRes, contactsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/testimonials`),
      ]);

      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem('admin_token');
    const { type, id } = deleteDialog;

    try {
      await axios.delete(`${API}/${type}`, {
        data: { id },
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Item deleted successfully');
      if (type === 'bookings') {
        setBookings((current) => current.filter((item) => item.id !== id));
      } else if (type === 'contact') {
        setContacts((current) => current.filter((item) => item.id !== id));
      } else if (type === 'testimonials') {
        setTestimonials((current) => current.filter((item) => item.id !== id));
      }
      setDeleteDialog({ open: false, type: '', id: '' });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    const token = localStorage.getItem('admin_token');

    try {
      await axios.patch(
        `${API}/bookings/status`,
        { id: bookingId, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Status updated to ${newStatus}`);
      setBookings((current) =>
        current.map((booking) =>
          booking.id === bookingId ? { ...booking, status: newStatus } : booking
        )
      );
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleContactStatusChange = async (contactId, newStatus) => {
    const token = localStorage.getItem('admin_token');

    try {
      await axios.patch(
        `${API}/contact/status`,
        { id: contactId, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Contact status updated to ${newStatus}`);
      setContacts((current) =>
        current.map((contact) =>
          contact.id === contactId ? { ...contact, status: newStatus } : contact
        )
      );
    } catch (error) {
      console.error('Contact status update error:', error);
      toast.error('Failed to update contact status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'read':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'working':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'finished':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'responded':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    if (statusFilter === 'all') return true;
    return (booking.status || 'new') === statusFilter;
  });

  const filteredContacts = contacts.filter((contact) => {
    if (contactStatusFilter === 'all') return true;
    return (contact.status || 'new') === contactStatusFilter;
  });

  const getStatusCount = (status) => {
    if (status === 'all') return bookings.length;
    return bookings.filter((b) => (b.status || 'new') === status).length;
  };

  const getContactStatusCount = (status) => {
    if (status === 'all') return contacts.length;
    return contacts.filter((c) => (c.status || 'new') === status).length;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');

    try {
      await axios.post(
        `${API}/admin/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      toast.success('Logged out successfully');
      navigate('/admin');
    }
  };

  const renderFilterButton = (label, value, current, setCurrent, activeClass, inactiveClass, count) => (
    <button
      key={value}
      onClick={() => setCurrent(value)}
      className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${current === value ? activeClass : inactiveClass}`}
    >
      {label} ({count})
    </button>
  );

  const cardShell =
    'rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-6 transition duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_65px_-28px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))] dark:hover:border-cyan-300/60 dark:hover:shadow-[0_24px_65px_-28px_rgba(34,211,238,0.35)]';

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)]"
        data-testid="dashboard-loading"
      >
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
          <div className="h-14 w-14 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500 dark:border-white/10 dark:border-t-cyan-300" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white"
      data-testid="admin-dashboard"
    >
      <section className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              Admin control center
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="bg-red-500 text-white transition duration-300 hover:bg-red-600 self-start sm:self-auto"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>

          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl dark:text-white">
                Manage bookings, messages,
                <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  and testimonials from one place.
                </span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
                Review incoming activity, update statuses, generate invoices, and keep the
                operational side of Connectify moving smoothly.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/admin/invoice')}
                className="bg-[#1E90FF] text-white transition duration-300 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)]"
              >
                <ReceiptText className="mr-2 h-4 w-4" />
                Generate Invoice
              </Button>
              <Button
                onClick={() => navigate('/admin/invoices')}
                className="bg-slate-900 text-white transition duration-300 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <ClipboardList className="mr-2 h-4 w-4" />
                Invoice History
              </Button>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              {
                id: 'bookings',
                icon: Calendar,
                label: 'Bookings',
                value: bookings.length,
                iconClass: 'from-sky-500 to-cyan-400',
              },
              {
                id: 'contacts',
                icon: Mail,
                label: 'Messages',
                value: contacts.length,
                iconClass: 'from-emerald-500 to-teal-400',
              },
              {
                id: 'testimonials',
                icon: Star,
                label: 'Testimonials',
                value: testimonials.length,
                iconClass: 'from-amber-400 to-orange-400',
              },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                data-testid={`stats-${item.id}`}
                className={`${cardShell} text-left ${activeView === item.id ? 'ring-2 ring-sky-300 dark:ring-cyan-300/60' : ''}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${item.iconClass} text-white shadow-lg`}>
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-slate-950 dark:text-white">{item.value}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{item.label}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="admin-content">
          {activeView === 'bookings' && (
            <div data-testid="bookings-content">
              <div className={`${cardShell} mb-6`}>
                <div className="flex flex-wrap gap-2">
                  {[
                    ['All', 'all', 'bg-slate-900 text-white dark:bg-white dark:text-slate-950', 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10', getStatusCount('all')],
                    ['New', 'new', 'bg-blue-500 text-white', 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50', getStatusCount('new')],
                    ['Read', 'read', 'bg-yellow-500 text-white', 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50', getStatusCount('read')],
                    ['Working', 'working', 'bg-orange-500 text-white', 'bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:hover:bg-orange-900/50', getStatusCount('working')],
                    ['Finished', 'finished', 'bg-green-500 text-white', 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50', getStatusCount('finished')],
                  ].map(([label, value, activeClass, inactiveClass, count]) =>
                    renderFilterButton(label, value, statusFilter, setStatusFilter, activeClass, inactiveClass, count)
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {filteredBookings.length === 0 ? (
                  <div className={`${cardShell} text-center text-slate-600 dark:text-slate-300`}>
                    {statusFilter === 'all' ? 'No bookings yet' : `No ${statusFilter} bookings`}
                  </div>
                ) : (
                  filteredBookings.map((booking) => (
                    <div key={booking.id} data-testid={`booking-${booking.id}`} className={cardShell}>
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex-1">
                          <div className="mb-3 flex flex-wrap items-center gap-3">
                            <h3 className="text-xl font-bold text-slate-950 dark:text-white">{booking.name}</h3>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(booking.status || 'new')}`}>
                              {(booking.status || 'new').toUpperCase()}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                            <p><span className="font-semibold text-slate-800 dark:text-white">Email:</span> {booking.email}</p>
                            <p><span className="font-semibold text-slate-800 dark:text-white">Phone:</span> {booking.phone}</p>
                            <p><span className="font-semibold text-slate-800 dark:text-white">Service:</span> {booking.service_type}</p>
                            <p><span className="font-semibold text-slate-800 dark:text-white">Preferred Date:</span> {booking.preferred_date}</p>
                            {booking.message && (
                              <p><span className="font-semibold text-slate-800 dark:text-white">Message:</span> {booking.message}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="icon"
                          data-testid={`delete-booking-${booking.id}`}
                          onClick={() => setDeleteDialog({ open: true, type: 'bookings', id: booking.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200 pt-5 dark:border-white/10">
                        {(booking.status === 'new' || !booking.status) && (
                          <Button
                            size="sm"
                            data-testid={`status-read-${booking.id}`}
                            onClick={() => handleStatusChange(booking.id, 'read')}
                            className="bg-yellow-500 text-white hover:bg-yellow-600"
                          >
                            Mark as Read
                          </Button>
                        )}
                        <Button
                          size="sm"
                          data-testid={`status-working-${booking.id}`}
                          onClick={() => handleStatusChange(booking.id, 'working')}
                          className="bg-orange-500 text-white hover:bg-orange-600"
                        >
                          Mark as Working
                        </Button>
                        <Button
                          size="sm"
                          data-testid={`status-finished-${booking.id}`}
                          onClick={() => handleStatusChange(booking.id, 'finished')}
                          className="bg-green-500 text-white hover:bg-green-600"
                        >
                          Mark as Finished
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeView === 'contacts' && (
            <div data-testid="contacts-content">
              <div className={`${cardShell} mb-6`}>
                <div className="flex flex-wrap gap-2">
                  {[
                    ['All', 'all', 'bg-slate-900 text-white dark:bg-white dark:text-slate-950', 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10', getContactStatusCount('all')],
                    ['New', 'new', 'bg-blue-500 text-white', 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50', getContactStatusCount('new')],
                    ['Read', 'read', 'bg-yellow-500 text-white', 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50', getContactStatusCount('read')],
                    ['Responded', 'responded', 'bg-green-500 text-white', 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50', getContactStatusCount('responded')],
                  ].map(([label, value, activeClass, inactiveClass, count]) =>
                    renderFilterButton(label, value, contactStatusFilter, setContactStatusFilter, activeClass, inactiveClass, count)
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {filteredContacts.length === 0 ? (
                  <div className={`${cardShell} text-center text-slate-600 dark:text-slate-300`}>
                    {contactStatusFilter === 'all' ? 'No messages yet' : `No ${contactStatusFilter} messages`}
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <div key={contact.id} data-testid={`contact-${contact.id}`} className={cardShell}>
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex-1">
                          <div className="mb-3 flex flex-wrap items-center gap-3">
                            <h3 className="text-xl font-bold text-slate-950 dark:text-white">{contact.name}</h3>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(contact.status || 'new')}`}>
                              {(contact.status || 'new').toUpperCase()}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                            <p><span className="font-semibold text-slate-800 dark:text-white">Email:</span> {contact.email}</p>
                            <p><span className="font-semibold text-slate-800 dark:text-white">Phone:</span> {contact.phone}</p>
                            <p><span className="font-semibold text-slate-800 dark:text-white">Message:</span> {contact.message}</p>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="icon"
                          data-testid={`delete-contact-${contact.id}`}
                          onClick={() => setDeleteDialog({ open: true, type: 'contact', id: contact.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200 pt-5 dark:border-white/10">
                        {(contact.status === 'new' || !contact.status) && (
                          <Button
                            size="sm"
                            data-testid={`status-read-contact-${contact.id}`}
                            onClick={() => handleContactStatusChange(contact.id, 'read')}
                            className="bg-yellow-500 text-white hover:bg-yellow-600"
                          >
                            Mark as Read
                          </Button>
                        )}
                        <Button
                          size="sm"
                          data-testid={`status-responded-contact-${contact.id}`}
                          onClick={() => handleContactStatusChange(contact.id, 'responded')}
                          className="bg-green-500 text-white hover:bg-green-600"
                        >
                          Mark as Responded
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeView === 'testimonials' && (
            <div data-testid="testimonials-content" className="space-y-4">
              {testimonials.length === 0 ? (
                <div className={`${cardShell} text-center text-slate-600 dark:text-slate-300`}>No testimonials yet</div>
              ) : (
                testimonials.map((testimonial) => (
                  <div key={testimonial.id} data-testid={`testimonial-${testimonial.id}`} className={cardShell}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex-1">
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <h3 className="text-xl font-bold text-slate-950 dark:text-white">{testimonial.name}</h3>
                          <div className="flex">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-current text-amber-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm italic leading-7 text-slate-600 dark:text-slate-300">
                          "{testimonial.message}"
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        size="icon"
                        data-testid={`delete-testimonial-${testimonial.id}`}
                        onClick={() => setDeleteDialog({ open: true, type: 'testimonials', id: testimonial.id })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
